import React, { useState } from 'react'
import {
  Briefcase,
  Users,
  Clock,
  DollarSign,
  FileText,
  AlertTriangle,
  CheckCircle,
  Download,
  Plus,
  ShieldCheck,
  Building,
  TrendingUp,
  Upload,
  Calendar,
  Zap,
} from 'lucide-react'
import { AlphalistCompiler } from '@repo/entity-ledgersync'
import { LedgerSyncService } from '@repo/entity-ledgersync'
import type { PettyCashVoucher } from '@repo/entity-ledgersync'

export function LedgerSyncDashboard() {
  const [activeRole, setActiveRole] = useState<'owner' | 'partner' | 'employee' | 'client'>('owner')
  
  // State for Petty Cash Vouchers
  const [vouchers, setVouchers] = useState<PettyCashVoucher[]>([
    {
      id: 'pc-1',
      voucherNumber: 'PC-2026-001',
      requestedById: 'emp-101',
      requestedByName: 'Juan Dela Cruz',
      amount: 4500,
      purpose: 'BIR RDO 047 Document Filing Fees & Courier Services',
      category: 'bir_filing_fee',
      status: 'disbursed',
      requestedAt: Date.now() - 86400000 * 2,
    },
    {
      id: 'pc-2',
      voucherNumber: 'PC-2026-002',
      requestedById: 'emp-102',
      requestedByName: 'Maria Santos',
      amount: 42000,
      purpose: 'Client Onsite Audit Transportation Allowance',
      category: 'transportation',
      status: 'disbursed',
      requestedAt: Date.now() - 86400000,
    },
  ])

  // New Petty Cash Modal State
  const [showPettyCashModal, setShowPettyCashModal] = useState(false)
  const [newAmount, setNewAmount] = useState('1500')
  const [newPurpose, setNewPurpose] = useState('')
  const [newCategory, setNewCategory] = useState<'transportation' | 'office_supplies' | 'client_meeting' | 'bir_filing_fee' | 'other'>('bir_filing_fee')

  // BIR Alphalist Generator State
  const [selectedForm, setSelectedForm] = useState<'1604-C' | '1604-F' | '1604-E'>('1604-C')
  const [agentTin, setAgentTin] = useState('123456789')
  const [agentBranch, setAgentBranch] = useState('0000')
  const [agentRdo, setAgentRdo] = useState('047')
  const [agentName, setAgentName] = useState('SYCIP & ASSOCIATES ACCOUNTING FIRM')
  const [generatedDatContent, setGeneratedDatContent] = useState<string | null>(null)

  // Calculate Petty Cash Status
  const pettyCashStatus = LedgerSyncService.evaluatePettyCashFund(vouchers, 50000, 5000)

  // Sample Client Companies
  const [clients] = useState([
    {
      id: 'c-1',
      companyName: 'Manila Trading & Logistics Corp',
      tin: '987-654-321-0000',
      rdo: '047',
      retainer: 35000,
      status: 'active',
      missingTins: 0,
      assignedAccountant: 'Juan Dela Cruz',
      assignedPartner: 'Atty. Roberto Tan',
    },
    {
      id: 'c-2',
      companyName: 'BGC Tech Ventures Inc',
      tin: '456-789-123-0000',
      rdo: '044',
      retainer: 50000,
      status: 'active',
      missingTins: 2,
      assignedAccountant: 'Maria Santos',
      assignedPartner: 'Atty. Roberto Tan',
    },
    {
      id: 'c-3',
      companyName: 'Cebu Holdings & Retail Co',
      tin: '321-654-987-0000',
      rdo: '080',
      retainer: 28000,
      status: 'active',
      missingTins: 0,
      assignedAccountant: 'Juan Dela Cruz',
      assignedPartner: 'Elena Ramos',
    },
  ])

  // Sample Automation Triggers
  const automationRules = [
    {
      id: 'ar-1',
      name: 'Monthly Retainer Billing',
      trigger: '1st of Month at 8:00 AM',
      action: 'Generate and email monthly retainer invoices to all active clients',
      status: 'active',
    },
    {
      id: 'ar-2',
      name: 'BIR Tax Deadline Document Chaser',
      trigger: 'Tax Deadline - 5 Days',
      action: 'Scan client vaults and send automated reminder email for missing TINs/receipts',
      status: 'active',
    },
    {
      id: 'ar-3',
      name: 'Petty Cash Replenishment Alert',
      trigger: 'Petty Cash Balance < ₱5,000',
      action: 'Alert Managing Partner to disburse physical fund replenishment',
      status: 'active',
    },
    {
      id: 'ar-4',
      name: 'Weekly Collections Aggregator',
      trigger: 'Every Friday at 5:00 PM',
      action: 'Compile cash inflows into formatted PDF report and email to Owners/Partners',
      status: 'active',
    },
  ]

  const handleAddPettyCash = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newPurpose) return
    const newVoucher: PettyCashVoucher = {
      id: `pc-${Date.now()}`,
      voucherNumber: `PC-2026-${(vouchers.length + 1).toString().padStart(3, '0')}`,
      requestedById: 'emp-curr',
      requestedByName: 'Current User',
      amount: parseFloat(newAmount) || 0,
      purpose: newPurpose,
      category: newCategory,
      status: 'disbursed',
      requestedAt: Date.now(),
    }
    setVouchers([newVoucher, ...vouchers])
    setNewPurpose('')
    setShowPettyCashModal(false)
  }

  const handleGenerateAlphalist = () => {
    const dat = AlphalistCompiler.compileDatFile(
      {
        formType: selectedForm,
        withholdingAgentTin: agentTin,
        branchCode: agentBranch,
        rdoCode: agentRdo,
        taxableYear: 2025,
        agentName: agentName,
      },
      [
        {
          tin: '111222333000',
          branchCode: '0000',
          name: 'DELA CRUZ, JUAN PEDRO',
          atcCode: 'WI100',
          grossPayment: 450000,
          taxRatePercent: 15,
          taxWithheld: 67500,
        },
        {
          tin: '444555666000',
          branchCode: '0000',
          name: 'SANTOS, MARIA CLARA',
          atcCode: 'WI100',
          grossPayment: 520000,
          taxRatePercent: 20,
          taxWithheld: 104000,
        },
      ]
    )
    setGeneratedDatContent(dat)
  }

  const handleDownloadDat = () => {
    if (!generatedDatContent) return
    const blob = new Blob([generatedDatContent], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `1604C_123456789_0000_122025.dat`
    link.click()
  }

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 p-6 space-y-6">
      {/* Top Title Banner */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-slate-800/80 backdrop-blur border border-slate-700 p-6 rounded-2xl shadow-xl">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-600/20 text-blue-400 rounded-xl border border-blue-500/30">
              <Briefcase className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2">
                LedgerSync
                <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-blue-500/20 text-blue-300 border border-blue-400/30">
                  Philippines BIR Practice Management
                </span>
              </h1>
              <p className="text-sm text-slate-400">
                Multi-Tenant Practice Operating System • Alphalist .DAT Generator • Weekly Collections • Petty Cash
              </p>
            </div>
          </div>
        </div>

        {/* Role Selection Tabs */}
        <div className="flex items-center bg-slate-900/90 p-1.5 rounded-xl border border-slate-700/80">
          <button
            onClick={() => setActiveRole('owner')}
            className={`flex items-center gap-2 px-3.5 py-2 text-xs font-medium rounded-lg transition-all ${
              activeRole === 'owner'
                ? 'bg-blue-600 text-white shadow-md shadow-blue-900/40 font-semibold'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
            }`}
          >
            <TrendingUp className="w-3.5 h-3.5" />
            Owner (Partner)
          </button>
          <button
            onClick={() => setActiveRole('partner')}
            className={`flex items-center gap-2 px-3.5 py-2 text-xs font-medium rounded-lg transition-all ${
              activeRole === 'partner'
                ? 'bg-blue-600 text-white shadow-md shadow-blue-900/40 font-semibold'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
            }`}
          >
            <Users className="w-3.5 h-3.5" />
            Client Manager
          </button>
          <button
            onClick={() => setActiveRole('employee')}
            className={`flex items-center gap-2 px-3.5 py-2 text-xs font-medium rounded-lg transition-all ${
              activeRole === 'employee'
                ? 'bg-blue-600 text-white shadow-md shadow-blue-900/40 font-semibold'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
            }`}
          >
            <FileText className="w-3.5 h-3.5" />
            Accountant
          </button>
          <button
            onClick={() => setActiveRole('client')}
            className={`flex items-center gap-2 px-3.5 py-2 text-xs font-medium rounded-lg transition-all ${
              activeRole === 'client'
                ? 'bg-blue-600 text-white shadow-md shadow-blue-900/40 font-semibold'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
            }`}
          >
            <Building className="w-3.5 h-3.5" />
            Client Portal
          </button>
        </div>
      </div>

      {/* Petty Cash Threshold Alert Banner */}
      {pettyCashStatus.isBelowThreshold && (
        <div className="bg-amber-950/80 border-2 border-amber-500/80 p-4 rounded-xl flex items-center justify-between gap-4 text-amber-200 shadow-lg animate-pulse">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-400 shrink-0" />
            <div>
              <h4 className="font-bold text-amber-300 text-sm">
                AUTOMATION TRIGGER ALERT: Petty Cash Balance Below Threshold (&lt; ₱5,000)
              </h4>
              <p className="text-xs text-amber-200/90">
                Current Petty Cash Balance is <span className="font-bold text-white">₱{pettyCashStatus.currentBalance.toLocaleString()}</span>. 
                Managing Partner has been automatically alerted to disburse float replenishment.
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowPettyCashModal(true)}
            className="px-3.5 py-1.5 text-xs font-semibold bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-lg shrink-0 transition-colors"
          >
            Request Replenishment
          </button>
        </div>
      )}

      {/* Quick Status KPI Cards - Hidden for clients */}
      {activeRole !== 'client' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-slate-800/60 border border-slate-700/80 p-5 rounded-xl space-y-2">
            <div className="flex items-center justify-between text-slate-400">
              <span className="text-xs font-medium">Friday Collection Goal</span>
              <DollarSign className="w-4 h-4 text-emerald-400" />
            </div>
            <div className="text-2xl font-bold text-white">₱113,000</div>
            <div className="text-xs text-emerald-400 flex items-center gap-1 font-medium">
              <TrendingUp className="w-3 h-3" />
              88.5% weekly target collected
            </div>
          </div>

          {(activeRole === 'owner' || activeRole === 'employee') && (
            <div className="bg-slate-800/60 border border-slate-700/80 p-5 rounded-xl space-y-2">
              <div className="flex items-center justify-between text-slate-400">
                <span className="text-xs font-medium">Petty Cash Fund</span>
                <Clock className="w-4 h-4 text-amber-400" />
              </div>
              <div className={`text-2xl font-bold ${pettyCashStatus.isBelowThreshold ? 'text-amber-400' : 'text-white'}`}>
                ₱{pettyCashStatus.currentBalance.toLocaleString()}
              </div>
              <div className="text-xs text-slate-400 font-medium">
                Float limit: ₱50,000 • Alert threshold: &lt; ₱5,000
              </div>
            </div>
          )}

          <div className="bg-slate-800/60 border border-slate-700/80 p-5 rounded-xl space-y-2">
            <div className="flex items-center justify-between text-slate-400">
              <span className="text-xs font-medium">Active Retainer Clients</span>
              <Building className="w-4 h-4 text-blue-400" />
            </div>
            <div className="text-2xl font-bold text-white">{clients.length} Companies</div>
            <div className="text-xs text-blue-400 font-medium">
              ₱113,000 total monthly retainers
            </div>
          </div>

          <div className="bg-slate-800/60 border border-slate-700/80 p-5 rounded-xl space-y-2">
            <div className="flex items-center justify-between text-slate-400">
              <span className="text-xs font-medium">BIR Alphalist Status</span>
              <ShieldCheck className="w-4 h-4 text-indigo-400" />
            </div>
            <div className="text-2xl font-bold text-white">Forms 1604-C/F/E</div>
            <div className="text-xs text-emerald-400 font-medium flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              100% BIR Validation Ready
            </div>
          </div>
        </div>
      )}

      {/* Main Feature Modules Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Module 1: BIR Alphalist .DAT Generator (Hidden for clients) */}
        {activeRole !== 'client' && (
          <div className="lg:col-span-2 bg-slate-800/60 border border-slate-700/80 p-6 rounded-2xl space-y-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-blue-600/20 text-blue-400 rounded-lg">
                  <FileText className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-white text-base">BIR Alphalist .DAT File Compiler</h3>
                  <p className="text-xs text-slate-400">Automated generation of BIR-compliant .dat files for forms 1604-C, 1604-F, and 1604-E</p>
                </div>
              </div>
              <span className="px-2.5 py-1 text-xs font-medium bg-emerald-500/20 text-emerald-400 rounded-full border border-emerald-500/30">
                BIR Verified
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">Form Type</label>
                <select
                  value={selectedForm}
                  onChange={(e) => setSelectedForm(e.target.value as any)}
                  className="w-full bg-slate-900 border border-slate-700 text-slate-200 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="1604-C">Form 1604-C (Compensation Withholding)</option>
                  <option value="1604-F">Form 1604-F (Final Withholding)</option>
                  <option value="1604-E">Form 1604-E (Expanded Withholding)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">Withholding Agent TIN</label>
                <input
                  type="text"
                  value={agentTin}
                  onChange={(e) => setAgentTin(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 text-slate-200 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">RDO Code</label>
                <input
                  type="text"
                  value={agentRdo}
                  onChange={(e) => setAgentRdo(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 text-slate-200 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleGenerateAlphalist}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-medium text-xs rounded-lg transition-colors flex items-center gap-2 shadow-lg shadow-blue-900/30"
              >
                <Zap className="w-3.5 h-3.5" />
                Compile BIR .DAT File
              </button>
              {generatedDatContent && (
                <button
                  onClick={handleDownloadDat}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-medium text-xs rounded-lg transition-colors flex items-center gap-2"
                >
                  <Download className="w-3.5 h-3.5" />
                  Download .DAT File
                </button>
              )}
            </div>

            {generatedDatContent && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs text-slate-400">
                  <span>Compiled File Preview (Header, Details &amp; Control Records):</span>
                  <span className="text-emerald-400 font-medium">Ready for BIR Submission</span>
                </div>
                <pre className="bg-slate-950 p-4 rounded-xl text-xs text-emerald-400 font-mono overflow-x-auto border border-slate-800 max-h-48 leading-relaxed">
                  {generatedDatContent}
                </pre>
              </div>
            )}
          </div>
        )}

        {/* Module 2: Petty Cash Voucher Management (Visible to Owner, Employee) */}
        {(activeRole === 'owner' || activeRole === 'employee') && (
          <div className="bg-slate-800/60 border border-slate-700/80 p-6 rounded-2xl space-y-4 flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 bg-amber-500/20 text-amber-400 rounded-lg">
                    <DollarSign className="w-4 h-4" />
                  </div>
                  <h3 className="font-bold text-white text-base">Petty Cash Vouchers</h3>
                </div>
                <button
                  onClick={() => setShowPettyCashModal(true)}
                  className="p-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
                  title="New Voucher Request"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-2.5 max-h-64 overflow-y-auto pr-1">
                {vouchers.map((v) => (
                  <div key={v.id} className="bg-slate-900/80 border border-slate-700/60 p-3 rounded-xl space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-mono text-slate-400 font-semibold">{v.voucherNumber}</span>
                      <span className="font-bold text-amber-400">₱{v.amount.toLocaleString()}</span>
                    </div>
                    <p className="text-xs text-slate-200 line-clamp-1">{v.purpose}</p>
                    <div className="flex items-center justify-between text-[11px] text-slate-400">
                      <span>{v.requestedByName}</span>
                      <span className="text-emerald-400 font-medium capitalize">{v.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-slate-900/60 p-3 rounded-xl border border-slate-700/60 text-xs space-y-1 text-slate-300">
              <div className="flex justify-between">
                <span>Total Disbursed:</span>
                <span className="font-bold text-slate-100">
                  ₱{vouchers.reduce((s, v) => s + v.amount, 0).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between text-amber-400">
                <span>Current Fund Balance:</span>
                <span className="font-bold">₱{pettyCashStatus.currentBalance.toLocaleString()}</span>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Client Portfolio & Automation Engine */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Multi-Tenant Client Portfolio Vault */}
        {activeRole !== 'client' && (
          <div className="bg-slate-800/60 border border-slate-700/80 p-6 rounded-2xl space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-white text-base flex items-center gap-2">
                <Building className="w-5 h-5 text-blue-400" />
                Client Company Portfolios &amp; Vaults
              </h3>
              <span className="text-xs text-slate-400">Multi-Tenant RLS Isolated</span>
            </div>

            <div className="space-y-3">
              {clients.map((c) => (
                <div key={c.id} className="bg-slate-900/80 border border-slate-700/60 p-4 rounded-xl flex items-center justify-between gap-4">
                  <div className="space-y-1">
                    <h4 className="font-semibold text-white text-sm">{c.companyName}</h4>
                    <div className="flex items-center gap-3 text-xs text-slate-400 font-mono">
                      <span>TIN: {c.tin}</span>
                      <span>RDO: {c.rdo}</span>
                    </div>
                    <div className="text-xs text-slate-400">
                      Partner: <span className="text-slate-200">{c.assignedPartner}</span> • Accountant: <span className="text-slate-200">{c.assignedAccountant}</span>
                    </div>
                  </div>

                  <div className="text-right space-y-1">
                    <div className="text-sm font-bold text-emerald-400">₱{c.retainer.toLocaleString()}/mo</div>
                    {c.missingTins > 0 ? (
                      <span className="inline-block px-2 py-0.5 text-[10px] font-semibold bg-amber-500/20 text-amber-400 rounded-full border border-amber-500/30">
                        ⚠️ {c.missingTins} Missing TINs
                      </span>
                    ) : (
                      <span className="inline-block px-2 py-0.5 text-[10px] font-semibold bg-emerald-500/20 text-emerald-400 rounded-full border border-emerald-500/30">
                        ✓ Vault Complete
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Client Specific Portal View */}
        {activeRole === 'client' && (
          <div className="bg-slate-800/60 border border-slate-700/80 p-6 rounded-2xl space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-white text-base flex items-center gap-2">
                <Building className="w-5 h-5 text-blue-400" />
                My Company Portal
              </h3>
            </div>
            
            <div className="bg-slate-900/80 border border-slate-700/60 p-6 rounded-xl space-y-4">
              <div className="flex items-center justify-between border-b border-slate-800 pb-4">
                <div>
                  <h4 className="font-bold text-lg text-white">Manila Trading & Logistics Corp</h4>
                  <p className="text-sm text-slate-400 mt-1">TIN: 987-654-321-0000 | RDO: 047</p>
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-400 mb-1">Assigned Partner</div>
                  <div className="font-medium text-slate-200">Atty. Roberto Tan</div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50 text-center hover:bg-slate-800 transition-colors cursor-pointer">
                  <Upload className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                  <div className="text-sm font-medium text-white">Upload Documents</div>
                  <div className="text-xs text-slate-400 mt-1">Receipts & TINs</div>
                </div>
                <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700/50 text-center hover:bg-slate-800 transition-colors cursor-pointer">
                  <FileText className="w-6 h-6 text-indigo-400 mx-auto mb-2" />
                  <div className="text-sm font-medium text-white">My Tax Returns</div>
                  <div className="text-xs text-slate-400 mt-1">View Filed Forms</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Automation Triggers Engine (Owner Only) */}
        {activeRole === 'owner' && (
          <div className="bg-slate-800/60 border border-slate-700/80 p-6 rounded-2xl space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-white text-base flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-400" />
                Practice Automation Engine
              </h3>
              <span className="text-xs text-amber-400 font-semibold px-2 py-0.5 rounded bg-amber-500/10 border border-amber-500/30">
                IF/THEN Logic Active
              </span>
            </div>

            <div className="space-y-3">
              {automationRules.map((r) => (
                <div key={r.id} className="bg-slate-900/80 border border-slate-700/60 p-3.5 rounded-xl space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-sm text-slate-100">{r.name}</span>
                    <span className="text-[11px] font-mono px-2 py-0.5 rounded bg-blue-900/40 text-blue-300 border border-blue-700/50">
                      {r.trigger}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400">{r.action}</p>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>

      {/* New Petty Cash Modal */}
      {showPettyCashModal && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-slate-800 border border-slate-700 p-6 rounded-2xl w-full max-w-md space-y-4 shadow-2xl">
            <h3 className="text-lg font-bold text-white">Submit Petty Cash Request</h3>

            <form onSubmit={handleAddPettyCash} className="space-y-3 text-xs">
              <div>
                <label className="block text-slate-300 mb-1">Amount (PHP ₱)</label>
                <input
                  type="number"
                  value={newAmount}
                  onChange={(e) => setNewAmount(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-slate-300 mb-1">Category</label>
                <select
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value as any)}
                  className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg p-2 text-xs outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="bir_filing_fee">BIR Filing Fee / Taxes</option>
                  <option value="transportation">Client Onsite Transportation</option>
                  <option value="office_supplies">Office Supplies &amp; Printing</option>
                  <option value="client_meeting">Client Meeting Expense</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-slate-300 mb-1">Purpose &amp; Description</label>
                <textarea
                  value={newPurpose}
                  onChange={(e) => setNewPurpose(e.target.value)}
                  rows={3}
                  className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg p-2 text-xs outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Details of expense..."
                  required
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowPettyCashModal(false)}
                  className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg transition-colors"
                >
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
